import React, { useState, useEffect } from 'react';
import api from '../api';
import S3UsageMonitor from './S3UsageMonitor';

interface OperationLog {
  id: number;
  operation_type: string;
  s3_key: string;
  product_id: number;
  file_size: number;
  content_type: string;
  operation_status: string;
  response_time_ms: number;
  error_message?: string;
  error_code?: string;
  user_id?: number;
  created_at: string;
}

interface PerformanceMetrics {
  period_hours: number;
  total_operations: number;
  metrics?: {
    response_time_ms?: {
      min: number;
      max: number;
      avg: number;
      p50: number;
      p95: number;
      p99: number;
    };
    throughput?: {
      operations_per_hour: number;
      total_bytes_processed: number;
      avg_bytes_per_operation: number;
    };
    error_rate?: {
      total_errors: number;
      error_percentage: number;
    };
  };
}

const ImageManagementDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'usage' | 'operations' | 'performance'>('usage');
  const [operationLogs, setOperationLogs] = useState<OperationLog[]>([]);
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    operation_type: '',
    status: '',
    hours: 24
  });

  const fetchOperationLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.operation_type) params.append('operation_type', filters.operation_type);
      if (filters.status) params.append('status', filters.status);
      params.append('hours', filters.hours.toString());
      params.append('limit', '50');

      const response = await api.get(`/api/images/operation-logs?${params}`);
      setOperationLogs(response.data?.logs || []);
    } catch (error) {
      console.error('Error fetching operation logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPerformanceMetrics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.operation_type) params.append('operation_type', filters.operation_type);
      params.append('hours', filters.hours.toString());

      const response = await api.get(`/api/images/performance-metrics?${params}`);
      setPerformanceMetrics(response.data);
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'operations') {
      fetchOperationLogs();
    } else if (activeTab === 'performance') {
      fetchPerformanceMetrics();
    }
  }, [activeTab, filters.operation_type, filters.status, filters.hours]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return '#28a745';
      case 'error': return '#dc3545';
      case 'pending': return '#ffc107';
      default: return '#6c757d';
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h3>Image Management Dashboard</h3>
      
      {/* Tab Navigation */}
      <div style={{ borderBottom: '1px solid #e2e8f0', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {[
            { key: 'usage', label: 'S3 Usage & Costs' },
            { key: 'operations', label: 'Operation Logs' },
            { key: 'performance', label: 'Performance Metrics' }
          ].map(tab => (
            <button
              key={tab.key}
              type="button"
              className={`btn ${activeTab === tab.key ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveTab(tab.key as any)}
              style={{ borderRadius: '4px 4px 0 0' }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Usage Tab */}
      {activeTab === 'usage' && (
        <S3UsageMonitor />
      )}

      {/* Operations Tab */}
      {activeTab === 'operations' && (
        <div>
          {/* Filters */}
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            marginBottom: '1rem', 
            padding: '1rem', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '4px' 
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '12px' }}>
                Operation Type
              </label>
              <select
                value={filters.operation_type}
                onChange={(e) => setFilters(prev => ({ ...prev, operation_type: e.target.value }))}
                style={{ padding: '0.25rem', fontSize: '12px' }}
              >
                <option value="">All Types</option>
                <option value="upload">Upload</option>
                <option value="download">Download</option>
                <option value="delete">Delete</option>
                <option value="list">List</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '12px' }}>
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                style={{ padding: '0.25rem', fontSize: '12px' }}
              >
                <option value="">All Status</option>
                <option value="success">Success</option>
                <option value="error">Error</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '12px' }}>
                Time Range
              </label>
              <select
                value={filters.hours}
                onChange={(e) => setFilters(prev => ({ ...prev, hours: parseInt(e.target.value) }))}
                style={{ padding: '0.25rem', fontSize: '12px' }}
              >
                <option value={1}>Last Hour</option>
                <option value={24}>Last 24 Hours</option>
                <option value={72}>Last 3 Days</option>
                <option value={168}>Last Week</option>
              </select>
            </div>
          </div>

          {/* Operation Logs Table */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>Time</th>
                    <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>Operation</th>
                    <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>Product ID</th>
                    <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>File</th>
                    <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>Size</th>
                    <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>Response Time</th>
                    <th style={{ padding: '0.5rem', border: '1px solid #dee2e6', textAlign: 'left' }}>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {operationLogs.map(log => (
                    <tr key={log.id}>
                      <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>
                        {formatDateTime(log.created_at)}
                      </td>
                      <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '3px',
                          backgroundColor: log.operation_type === 'upload' ? '#e3f2fd' :
                                          log.operation_type === 'download' ? '#f3e5f5' :
                                          log.operation_type === 'delete' ? '#ffebee' : '#f5f5f5',
                          fontSize: '10px'
                        }}>
                          {log.operation_type.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>
                        {log.product_id || '-'}
                      </td>
                      <td style={{ 
                        padding: '0.5rem', 
                        border: '1px solid #dee2e6',
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {log.s3_key ? log.s3_key.split('/').pop() : '-'}
                      </td>
                      <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>
                        {log.file_size ? formatFileSize(log.file_size) : '-'}
                      </td>
                      <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>
                        <span style={{
                          color: getStatusColor(log.operation_status),
                          fontWeight: 'bold'
                        }}>
                          {log.operation_status.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>
                        {log.response_time_ms}ms
                      </td>
                      <td style={{ 
                        padding: '0.5rem', 
                        border: '1px solid #dee2e6',
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {log.error_message || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {operationLogs.length === 0 && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  No operation logs found for the selected filters.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
          ) : performanceMetrics ? (
            <div>
              {/* Summary Cards */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '1rem',
                marginBottom: '2rem'
              }}>
                <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>Total Operations</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
                    {(performanceMetrics.total_operations || 0).toLocaleString()}
                  </div>
                </div>
                <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>Error Rate</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                    {(performanceMetrics.metrics?.error_rate?.error_percentage || 0).toFixed(1)}%
                  </div>
                </div>
                <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>Avg Response Time</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#17a2b8' }}>
                    {(performanceMetrics.metrics?.response_time_ms?.avg || 0).toFixed(0)}ms
                  </div>
                </div>
                <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>Data Processed</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6f42c1' }}>
                    {formatFileSize(performanceMetrics.metrics?.throughput?.total_bytes_processed || 0)}
                  </div>
                </div>
              </div>

              {/* Response Time Metrics */}
              {performanceMetrics.metrics?.response_time_ms && (
                <div style={{ marginBottom: '2rem' }}>
                  <h5>Response Time Distribution</h5>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
                    gap: '1rem'
                  }}>
                    {[
                      { label: 'Min', value: performanceMetrics.metrics.response_time_ms.min },
                      { label: 'Average', value: performanceMetrics.metrics.response_time_ms.avg },
                      { label: 'P50', value: performanceMetrics.metrics.response_time_ms.p50 },
                      { label: 'P95', value: performanceMetrics.metrics.response_time_ms.p95 },
                      { label: 'P99', value: performanceMetrics.metrics.response_time_ms.p99 },
                      { label: 'Max', value: performanceMetrics.metrics.response_time_ms.max }
                    ].map(metric => (
                      <div key={metric.label} style={{ 
                        padding: '0.75rem', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '4px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '12px', color: '#666' }}>{metric.label}</div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                          {(metric.value || 0).toFixed(0)}ms
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Throughput Metrics */}
              {performanceMetrics.metrics?.throughput && (
                <div style={{ marginBottom: '2rem' }}>
                  <h5>Throughput Metrics</h5>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '1rem'
                  }}>
                    <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                      <div style={{ fontSize: '14px', color: '#666' }}>Operations/Hour</div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#007bff' }}>
                        {(performanceMetrics.metrics?.throughput?.operations_per_hour || 0).toFixed(1)}
                      </div>
                    </div>
                    <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                      <div style={{ fontSize: '14px', color: '#666' }}>Avg Bytes/Operation</div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#28a745' }}>
                        {formatFileSize(performanceMetrics.metrics?.throughput?.avg_bytes_per_operation || 0)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Information */}
              {performanceMetrics.metrics?.error_rate && (
                <div style={{ marginBottom: '2rem' }}>
                  <h5>Error Analysis</h5>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                    gap: '1rem'
                  }}>
                    <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                      <div style={{ fontSize: '14px', color: '#666' }}>Total Errors</div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#dc3545' }}>
                        {performanceMetrics.metrics?.error_rate?.total_errors || 0}
                      </div>
                    </div>
                    <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                      <div style={{ fontSize: '14px', color: '#666' }}>Error Rate</div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#dc3545' }}>
                        {(performanceMetrics.metrics?.error_rate?.error_percentage || 0).toFixed(2)}%
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Period Information */}
              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '4px',
                fontSize: '14px',
                color: '#666'
              }}>
                <strong>Analysis Period:</strong> Last {performanceMetrics.period_hours || filters.hours} hours
                {performanceMetrics.total_operations === 0 && (
                  <div style={{ marginTop: '0.5rem', color: '#dc3545' }}>
                    No operations found in this time period. Try uploading some images or extending the time range.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              No performance data available. Try uploading some images first.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageManagementDashboard;